import { useIsFetching, useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, redirect, stripSearchParams } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";

import {
	formatFileSize,
	INTAKE_DOCUMENT_STATUSES,
	INTAKE_FOLDERS,
	INTAKE_QUERY_MAX_LENGTH,
	intakeFolderLabel,
	type IntakeDocumentStatus,
	type IntakeFolder,
	type IntakeListedFile,
} from "@ravnar/api-contract";

import { ApiError } from "../../api/client";
import { downloadIntakeFile, intakeFilesQueryOptions, pendingIntakeFilesQueryOptions } from "../../api/intake";
import { DownloadIcon } from "../../components/icons";
import { PageHeader } from "../../components/shell/app-shell";
import { IconButton } from "../../components/ui/button";
import { buttonClassName } from "../../components/ui/button-styles";
import { Card, CardHeader } from "../../components/ui/card";
import { Empty } from "../../components/ui/empty";
import { Input } from "../../components/ui/field";
import { FilterPill, Toolbar } from "../../components/ui/filter-pill";
import { Pager } from "../../components/ui/pager";
import { Badge, Pill } from "../../components/ui/pill";
import { Table, type TableColumn } from "../../components/ui/table";
import { Updating } from "../../components/ui/updating";
import { INTAKE_STATUS_LABELS, INTAKE_STATUS_TONES, intakeFolderDotClass } from "../../domain/intake";
import { cn } from "../../lib/cn";
import { formatRelativeTime } from "../../lib/format";
import { kindForFileName } from "../../lib/intake";
import { FolderList, FolderTrail, type FolderFilters } from "./-components/intake-folders";
import { IntakeUploader } from "./-components/intake-uploader";

const intakeSearchSchema = z.object({
	status: z.enum(INTAKE_DOCUMENT_STATUSES).optional().catch(undefined),
	/** Only what the signed-in member uploaded; absent rather than false when the filter is off. */
	mine: z.boolean().optional().catch(undefined),
	/** The open folder of the document type catalogue; the top of Intake when there is none. */
	folder: z.enum(INTAKE_FOLDERS).optional().catch(undefined),
	/** What the search box holds. */
	q: z.string().default("").catch(""),
	/** A page link from the API, which the page never reads; the newest page when there is none. */
	page: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/intake/")({
	validateSearch: intakeSearchSchema,
	search: { middlewares: [stripSearchParams({ q: "" })] },
	loaderDeps: ({ search: { status, mine, folder, q, page } }) => ({ status, mine, folder, q, page }),
	// Prefetch through the Query cache so the page can use useSuspenseQuery without a loading flash.
	loader: async ({ context, deps }) => {
		try {
			await Promise.all([
				context.queryClient.query({ ...intakeFilesQueryOptions(deps), staleTime: "static" }),
				context.queryClient.query({ ...pendingIntakeFilesQueryOptions(), staleTime: "static" }),
			]);
		} catch (error) {
			// A page link the API no longer takes, from an old bookmark or edited by hand: start at the newest page.
			if (deps.page !== undefined && error instanceof ApiError && error.status === 400) {
				const { page: _page, ...filters } = deps;
				throw redirect({ to: "/intake", search: filters, replace: true });
			}
			throw error;
		}
	},
	head: () => ({ meta: [{ title: "Intake · Ravnar" }] }),
	staticData: { crumbs: ["Intake"] },
	component: IntakePage,
});

/** A file as the table shows it: the API's file, with the folder it is in. */
interface IntakeRow {
	readonly file: IntakeListedFile;
	/** The file's status: the query this page reads only ever answers with a document's. */
	readonly status: IntakeDocumentStatus;
	/** Badge text, taken from the file extension: "PDF", "PNG", "DOCX". */
	readonly kind: string;
}

/** What Document Filing read of who a document is with and its number, as a line under its name: "Protokol d.o.o. · 2026-0912". */
function readOf(file: IntakeListedFile): string | undefined {
	const read = [file.counterparty, file.number].filter((value): value is string => value !== undefined);
	return read.length === 0 ? undefined : read.join(" · ");
}

/**
 * The documents table's columns, as a function rather than a module-level constant: the trailing Download column needs
 * a handler and the id of whichever row is downloading, and the module otherwise stays free of component state.
 */
function documentColumns(
	onDownload: (row: IntakeRow) => void,
	downloadingId: string | undefined,
): readonly TableColumn<IntakeRow>[] {
	return [
		{
			key: "name",
			header: "Document",
			cell: row => {
				const read = readOf(row.file);
				return (
					<span className="flex items-center gap-2.5">
						<span
							aria-hidden="true"
							className={cn(
								"h-[7px] w-[7px] shrink-0 rounded-full",
								intakeFolderDotClass(row.file.filedIn),
							)}
						/>
						<span className="min-w-0">
							<Link
								to="/intake/$fileId"
								params={{ fileId: row.file.id }}
								className="block font-medium text-ink hover:underline"
							>
								{row.file.name}
							</Link>
							{read === undefined ? null : <span className="block text-caption text-ink-3">{read}</span>}
						</span>
					</span>
				);
			},
		},
		{ key: "kind", header: "Kind", cell: row => <Badge>{row.kind}</Badge> },
		{
			key: "filedAs",
			header: "Filed as",
			muted: true,
			cell: row => intakeFolderLabel(row.file.filedIn),
		},
		{ key: "uploadedBy", header: "Uploaded by", muted: true, cell: row => row.file.uploadedBy.name },
		{ key: "size", header: "Size", align: "right", cell: row => formatFileSize(row.file.size) },
		{
			key: "added",
			header: "Added",
			align: "right",
			muted: true,
			cell: row => formatRelativeTime(row.file.uploadedAt),
		},
		{
			key: "status",
			header: "Status",
			align: "right",
			cell: row => <Pill tone={INTAKE_STATUS_TONES[row.status]}>{INTAKE_STATUS_LABELS[row.status]}</Pill>,
		},
		{
			key: "download",
			header: "Download",
			align: "right",
			cell: row => (
				<span className="flex justify-end">
					<IconButton
						aria-label={`Download ${row.file.name}`}
						disabled={downloadingId !== undefined}
						onClick={() => {
							onDownload(row);
						}}
					>
						<DownloadIcon />
					</IconButton>
				</span>
			),
		},
	];
}

/**
 * What an empty folder beside the domains says: what it would hold, since its name would not read in "Nothing is in …
 * yet." ("Nothing is in Not filed yet yet.").
 */
const EMPTY_SPECIAL_FOLDER: Readonly<Partial<Record<IntakeFolder, string>>> = {
	"needs-review": "No document needs review.",
	"out-of-scope": "No document is out of scope.",
	"not-filed": "Nothing is waiting to be filed.",
};

/**
 * What the tray says when it lists nothing: that no document holds the search, that the filters hide everything, that
 * the open folder holds nothing yet, whether an upload is still on its way (a file the API has but the copier has not
 * stored, which is not a document yet), or that there is nothing at all.
 */
function emptyMessage(folder: IntakeFolder | undefined, q: string, filtered: boolean, onTheWay: number): string {
	if (q.trim() !== "") {
		return "No document's name, nor what Document Filing read of it, holds your search.";
	}
	if (filtered) {
		return "No documents match these filters.";
	}
	if (folder !== undefined) {
		return EMPTY_SPECIAL_FOLDER[folder] ?? `Nothing is in ${intakeFolderLabel(folder)} yet.`;
	}
	return onTheWay > 0
		? "Nothing has landed yet; an upload appears here once it is stored."
		: "Nothing has been uploaded yet.";
}

function IntakePage() {
	const { status, mine, folder, q, page: pageToken } = Route.useSearch();
	const navigate = Route.useNavigate();
	const queryClient = useQueryClient();
	// What has been typed into the search box, kept by the box rather than read back from the URL, as the Directory's
	// is: the API answers the search, so the navigation a keystroke starts arrives only with the answer, and a box fed
	// from the URL would put older text back over newer. A search that reaches the URL while nobody is typing came
	// from elsewhere — a link, the back button — so the box takes it.
	const [typed, setTyped] = useState(q);
	const [isTyping, setIsTyping] = useState(false);
	const [shownQ, setShownQ] = useState(q);
	if (q !== shownQ) {
		setShownQ(q);
		if (!isTyping) {
			setTyped(q);
		}
	}
	const filesQuery = intakeFilesQueryOptions({ status, mine, folder, q, page: pageToken });
	const { data: page } = useSuspenseQuery(filesQuery);
	const { data: pending } = useSuspenseQuery(pendingIntakeFilesQueryOptions());
	const isUpdating = useIsFetching({ queryKey: filesQuery.queryKey, exact: true }) > 0;
	const [downloadFailed, setDownloadFailed] = useState<string | undefined>(undefined);
	const download = useMutation({
		mutationFn: (row: IntakeRow) => downloadIntakeFile(row.file.id),
		onMutate: () => {
			setDownloadFailed(undefined);
		},
		onError: (error, row) => {
			setDownloadFailed(
				error instanceof ApiError ? error.message : `${row.file.name} could not be downloaded. Try again.`,
			);
		},
	});
	// In AWS the copier files an upload seconds after the PUT: when a file that was on its way is filed, or gone
	// because it was a duplicate, the list is asked again so the newly filed document appears without a reload, and the
	// bell is asked again so its notification shows too. The documents list does watch a document that has landed
	// (`documentRefetchInterval`), but that only ever inspects the files its own answer already contains; a pending
	// file is not in that answer at all, so this effect is what catches the moment one leaves the pending list.
	const onTheWay = pending.length;
	const previousOnTheWayRef = useRef(onTheWay);
	useEffect(() => {
		if (onTheWay < previousOnTheWayRef.current) {
			void queryClient.invalidateQueries({ queryKey: ["intake"] });
			void queryClient.invalidateQueries({ queryKey: ["notifications"] });
		}
		previousOnTheWayRef.current = onTheWay;
	}, [onTheWay, queryClient]);
	// Every file this page reads is a document: the API answers this query with only the document statuses.
	const rows: readonly IntakeRow[] = page.files.map(file => ({
		file,
		status: file.status as IntakeDocumentStatus,
		kind: kindForFileName(file.name),
	}));
	const filtered = status !== undefined || mine === true;
	// What every folder link keeps: opening a folder narrows the list, it never drops a filter or the search.
	const filters: FolderFilters = { status, mine, q };
	// Every Download button is disabled while one download is on its way, not only the row it is for: pressing another
	// before the first has answered would record two audit entries for what looks like one click.
	const downloadingId = download.isPending ? download.variables.file.id : undefined;
	const columns = documentColumns(row => {
		download.mutate(row);
	}, downloadingId);

	return (
		<>
			<PageHeader
				title="Intake"
				description="Drop what arrives — invoices, contracts, receipts. Document Filing reads each one and files it."
			/>
			<IntakeUploader />
			<Toolbar
				end={
					<div className="w-[200px]">
						<Input
							aria-label="Search documents"
							placeholder="Search documents"
							maxLength={INTAKE_QUERY_MAX_LENGTH}
							value={typed}
							onFocus={() => {
								setIsTyping(true);
							}}
							onBlur={() => {
								setIsTyping(false);
							}}
							onChange={event => {
								const text = event.target.value;
								setTyped(text);
								// Every keystroke starts the list again from its newest page, in the same folder.
								void navigate({
									search: previous => ({ ...previous, q: text, page: undefined }),
									replace: true,
								});
							}}
						/>
					</div>
				}
			>
				<FilterPill
					active={!filtered}
					onClick={() => {
						void navigate({
							search: previous => ({ ...previous, status: undefined, mine: undefined, page: undefined }),
							replace: true,
						});
					}}
				>
					All
				</FilterPill>
				<span aria-hidden="true" className="mx-1 h-5 w-px bg-line" />
				{INTAKE_DOCUMENT_STATUSES.map(id => (
					<FilterPill
						key={id}
						active={status === id}
						onClick={() => {
							void navigate({
								search: previous => ({
									...previous,
									status: status === id ? undefined : id,
									page: undefined,
								}),
								replace: true,
							});
						}}
					>
						{INTAKE_STATUS_LABELS[id]}
					</FilterPill>
				))}
				<span aria-hidden="true" className="mx-1 h-5 w-px bg-line" />
				<FilterPill
					active={mine === true}
					onClick={() => {
						void navigate({
							search: previous => ({
								...previous,
								mine: mine === true ? undefined : true,
								page: undefined,
							}),
							replace: true,
						});
					}}
				>
					My Files
				</FilterPill>
			</Toolbar>
			<FolderTrail folder={folder} filters={filters} />
			{page.folders.length === 0 ? null : (
				<Card className="mb-[22px]">
					<CardHeader title={folder === undefined ? "Folders" : intakeFolderLabel(folder)} />
					<FolderList folders={page.folders} filters={filters} />
				</Card>
			)}
			<Card>
				<CardHeader
					title={`${String(page.total)} ${page.total === 1 ? "document" : "documents"}`}
					action={<Updating active={isUpdating} />}
				/>
				{rows.length > 0 ? (
					<Table label="Documents" columns={columns} rows={rows} rowKey={row => row.file.id} />
				) : (
					<Empty>{emptyMessage(folder, q, filtered, onTheWay)}</Empty>
				)}
				{page.next === null && page.previous === null ? null : (
					<Pager
						label="Intake pages"
						previous={
							page.previous === null ? null : (
								<Link
									to="/intake"
									search={{ ...filters, folder, page: page.previous }}
									className={buttonClassName({ size: "sm" })}
								>
									Previous
								</Link>
							)
						}
						next={
							page.next === null ? null : (
								<Link
									to="/intake"
									search={{ ...filters, folder, page: page.next }}
									className={buttonClassName({ size: "sm" })}
								>
									Next
								</Link>
							)
						}
					/>
				)}
			</Card>
			{downloadFailed === undefined ? null : (
				<p role="alert" className="mt-2 text-caption text-alert">
					{downloadFailed}
				</p>
			)}
		</>
	);
}
